import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { meQuery, projectQuery, projectsQuery } from "./api/queries";
import { ApiError } from "./api/http";
import { RootLayout, NotFound } from "./routes/root";
import { SignInPage, SignUpPage } from "./routes/auth";
import { OnboardingLayout, CreateOrgPage, CreateProjectPage } from "./routes/onboarding";
import { ProjectLayout, ProjectError } from "./routes/project-layout";
import { ComingSoonPage } from "./routes/coming-soon";
import { InstallPage } from "./routes/install";
import { InsightsLayout, InsightSoon } from "./routes/insights/layout";
import { SettingsLayout } from "./routes/settings/layout";
import { LAST_PROJECT_KEY } from "./lib/workspace";
import { prefs } from "./lib/storage";
import type { SectionKey } from "./nav";

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});

async function requireMe(queryClient: QueryClient, href: string) {
  const me = await queryClient.ensureQueryData(meQuery);
  if (!me) throw redirect({ to: "/sign-in", search: { redirect: href } });
  return me;
}

/** Sends a signed-in user to their last project, or to the next onboarding step. */
async function landingFor(queryClient: QueryClient): Promise<never> {
  const me = await queryClient.ensureQueryData(meQuery);
  if (!me) throw redirect({ to: "/sign-in", search: {} });
  const firstOrg = me.orgs[0];
  if (!firstOrg) throw redirect({ to: "/onboarding/org" });
  const last = prefs.get(LAST_PROJECT_KEY);
  if (last) {
    try {
      await queryClient.ensureQueryData(projectQuery(last));
      throw redirect({ to: "/p/$projectId", params: { projectId: last } });
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  }
  for (const org of me.orgs) {
    const projects = await queryClient.ensureQueryData(projectsQuery(org.id));
    const p = projects[0];
    if (p) throw redirect({ to: "/p/$projectId", params: { projectId: p.id } });
  }
  throw redirect({ to: "/onboarding/project", search: { org: firstOrg.id } });
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: ({ context }) => landingFor(context.queryClient),
});

const authSearch = z.object({ redirect: z.string().optional() });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: authSearch,
  beforeLoad: async ({ context }) => {
    if (await context.queryClient.ensureQueryData(meQuery)) await landingFor(context.queryClient);
  },
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  beforeLoad: async ({ context }) => {
    if (await context.queryClient.ensureQueryData(meQuery)) await landingFor(context.queryClient);
  },
  component: SignUpPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: async ({ context, location }) => ({ me: await requireMe(context.queryClient, location.href) }),
  component: OnboardingLayout,
});

const onboardingOrgRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/org",
  component: CreateOrgPage,
});

const onboardingProjectRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/project",
  validateSearch: z.object({ org: z.string().optional() }),
  component: CreateProjectPage,
});

export const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/p/$projectId",
  beforeLoad: async ({ context, location }) => ({ me: await requireMe(context.queryClient, location.href) }),
  loader: async ({ context, params }) => {
    const project = await context.queryClient.ensureQueryData(projectQuery(params.projectId));
    prefs.set(LAST_PROJECT_KEY, project.id);
    return { project };
  },
  component: ProjectLayout,
  errorComponent: ProjectError,
});

const homeRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./routes/home"), "HomePage"),
});
const installRoute = createRoute({ getParentRoute: () => projectRoute, path: "/install", component: InstallPage });

const insightsRoute = createRoute({ getParentRoute: () => projectRoute, path: "/insights", component: InsightsLayout });
const insightsIndexRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/p/$projectId/insights/trends", params });
  },
});
const trendsRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/trends",
  component: lazyRouteComponent(() => import("./routes/insights/trends"), "TrendsPage"),
});
const funnelsRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/funnels",
  component: lazyRouteComponent(() => import("./routes/insights/funnels"), "FunnelsPage"),
});
const retentionRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/retention",
  component: lazyRouteComponent(() => import("./routes/insights/retention"), "RetentionPage"),
});
const pathsRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/paths",
  component: lazyRouteComponent(() => import("./routes/insights/paths"), "PathsPage"),
});
const insightSoonRoute = createRoute({
  getParentRoute: () => insightsRoute,
  path: "/$kind",
  component: InsightSoon,
});

const settingsRoute = createRoute({ getParentRoute: () => projectRoute, path: "/settings", component: SettingsLayout });
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/p/$projectId/settings/project", params });
  },
});
const settingsProjectRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/project",
  component: lazyRouteComponent(() => import("./routes/settings/project"), "ProjectSettingsPage"),
});
const settingsTokensRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/tokens",
  component: lazyRouteComponent(() => import("./routes/settings/tokens"), "TokensPage"),
});

const soon = <K extends SectionKey>(key: K) =>
  createRoute({
    getParentRoute: () => projectRoute,
    path: `/${key}` as `/${K}`,
    component: () => <ComingSoonPage section={key} />,
  });
const soonRoutes = [
  soon("ask"),
  soon("dashboards"),
  soon("replays"),
  soon("people"),
  soon("cohorts"),
  soon("revenue"),
  soon("flags"),
  soon("experiments"),
  soon("catalog"),
  soon("findings"),
] as const;

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  signUpRoute,
  onboardingRoute.addChildren([onboardingOrgRoute, onboardingProjectRoute]),
  projectRoute.addChildren([
    homeRoute,
    installRoute,
    insightsRoute.addChildren([insightsIndexRoute, trendsRoute, funnelsRoute, retentionRoute, pathsRoute, insightSoonRoute]),
    settingsRoute.addChildren([settingsIndexRoute, settingsProjectRoute, settingsTokensRoute]),
    ...soonRoutes,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
